/**
 * Navigation sidebar for browsing through patient's call sessions.
 * Displays list of previous calls with date, duration, and status information.
 * Handles session selection and updates the main view via CallSessionContext.
 * Uses utility functions for date/duration formatting and StatusBadge for status display.
 */


import React from 'react';
import { useCallSessionContext } from '../../context/CallSessionContext';
import StatusBadge from '../common/StatusBadge';
import { formatDateShort, formatDuration } from '../../utils/dateUtils';

const CallSessionSidebar = ({ callSessions, selectedId }) => {
  const { selectCallSession } = useCallSessionContext();

  // Ensure call sessions are sorted by date (latest first)
  const sortedSessions = [...callSessions].sort((a, b) => {
    return new Date(b.call_date) - new Date(a.call_date);
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Previous Calls</h3>
      </div>
      
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto space-y-2 pr-2">
        {sortedSessions.length === 0 ? (
          <p className="text-gray-500 text-sm">No previous calls.</p>
        ) : (
          sortedSessions.map((session, index) => (
            <div
              key={session.id || `${session.call_date}-${index}`}
              className={`p-3 rounded-lg cursor-pointer transition-colors duration-200 ${
                session.id === selectedId ? 'bg-blue-100 text-blue-800 font-semibold' : 'bg-white hover:bg-gray-50 text-gray-700'
              }`}
              onClick={() => selectCallSession(session.id)}
            >
              <p className="text-sm">{formatDateShort(session.call_date)}</p>
              <p className="text-xs text-gray-500">Duration: {formatDuration(session.duration_in_seconds)}</p>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                session.patient_assessment_flag === 'stable' 
                  ? 'bg-green-100 text-green-800' 
                  : session.patient_assessment_flag === 'critical'
                  ? 'bg-red-100 text-red-800'
                  : session.patient_assessment_flag === 'follow-up'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {session.patient_assessment_flag 
                  ? session.patient_assessment_flag.charAt(0).toUpperCase() + session.patient_assessment_flag.slice(1)
                  : 'Unknown'}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CallSessionSidebar;